import { useState } from "react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import GeneralTab from "./components/GeneralTab";
import SectionsTab from "./components/SectionsTab";
import NavTab from "./components/NavTab";
import FooterTab from "./components/FooterTab";
import AuthorTab from "./components/AuthorTab";
import RankingsTab from "./components/RankingsTab";
import ArticlesTab from "./components/ArticlesTab";
import SaleTab from "./components/SaleTab";


const TABS = [
  { id: "general", label: "基本設定", icon: "ri-settings-3-line" },
  { id: "sections", label: "セクション管理", icon: "ri-layout-grid-line" },
  { id: "nav", label: "ナビゲーション", icon: "ri-menu-2-line" },
  { id: "rankings", label: "ランキング", icon: "ri-bar-chart-line" },
  { id: "articles", label: "最新記事", icon: "ri-article-line" },
  { id: "sale", label: "セール情報", icon: "ri-price-tag-3-line" },
  { id: "footer", label: "フッター", icon: "ri-layout-bottom-line" },
  { id: "author", label: "著者プロフィール", icon: "ri-user-line" },

];

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState("general");
  const { saveSettings, resetSettings, isDirty, isSaving, isLoading, saveError } = useSiteSettings();
  const [saved, setSaved] = useState(false);
  const [showReset, setShowReset] = useState(false);

  const handleSave = async () => {
    await saveSettings();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = async () => {
    await resetSettings();
    setShowReset(false);
  };

  const renderTab = () => {
    switch (activeTab) {
      case "general": return <GeneralTab />;
      case "sections": return <SectionsTab />;
      case "nav": return <NavTab />;
      case "rankings": return <RankingsTab />;
      case "articles": return <ArticlesTab />;
      case "sale": return <SaleTab />;
      case "footer": return <FooterTab />;
      case "author": return <AuthorTab />;

      default: return <GeneralTab />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* トップバー */}
      <div className="w-full bg-white border-b border-gray-200 flex items-center justify-between px-6 py-3 sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 flex items-center justify-center bg-red-600 rounded-md">
            <i className="ri-settings-3-line text-white text-sm" />
          </div>
          <span className="font-bold text-gray-800 text-sm">サイト管理パネル</span>
          {isLoading && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
              <i className="ri-loader-4-line animate-spin text-xs" /> 読み込み中...
            </span>
          )}
          {!isLoading && isDirty && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">未保存の変更あり</span>
          )}
          {saveError && (
            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1">
              <i className="ri-error-warning-line" /> {saveError}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 border border-gray-200 rounded-md cursor-pointer whitespace-nowrap"
          >
            <i className="ri-eye-line" /> サイトを確認
          </Link>
          <button
            onClick={() => setShowReset(true)}
            disabled={isSaving}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-red-600 border border-gray-200 rounded-md cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className="ri-restart-line" /> 初期化
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !isDirty}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-md cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 ${
              saved
                ? "bg-green-500 text-white"
                : isSaving
                ? "bg-gray-400 text-white"
                : isDirty
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-gray-200 text-gray-500"
            }`}
          >
            <i className={isSaving ? "ri-loader-4-line animate-spin" : saved ? "ri-check-line" : "ri-save-line"} />
            {isSaving ? "保存中..." : saved ? "保存しました！" : "保存する"}
          </button>
        </div>
      </div>

      <div className="flex flex-1">
        {/* サイドバー */}
        <aside className="w-52 bg-white border-r border-gray-200 flex-shrink-0 sticky top-[49px] h-[calc(100vh-49px)] overflow-y-auto">
          <nav className="p-2 space-y-0.5">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-left transition-colors cursor-pointer ${
                  activeTab === tab.id
                    ? "bg-red-50 text-red-700 font-medium"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className={`${tab.icon} text-sm`} />
                </div>
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* メインコンテンツ */}
        <main className="flex-1 p-6 max-w-3xl">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-24 text-gray-400">
              <i className="ri-loader-4-line animate-spin text-3xl mb-3" />
              <p className="text-sm">設定を読み込み中...</p>
            </div>
          ) : (
            renderTab()
          )}
        </main>
      </div>

      {/* リセット確認モーダル */}
      {showReset && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-3">
              <i className="ri-alert-line text-red-600 text-xl" />
            </div>
            <h3 className="text-center font-bold text-gray-800 mb-2">設定を初期化しますか？</h3>
            <p className="text-center text-xs text-gray-500 mb-5">全ての設定が初期値に戻ります。この操作は元に戻せません。</p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowReset(false)}
                className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer"
              >
                キャンセル
              </button>
              <button
                onClick={handleReset}
                disabled={isSaving}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 cursor-pointer disabled:opacity-50"
              >
                {isSaving ? "処理中..." : "初期化する"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
